import React, { useState, useEffect, useRef } from "react"
import { XMarkIcon, ClipboardIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import ReactDOM from "react-dom"

const AudioPreview = ({
  isOpen,
  audioUrl,
  fileName,
  onClose,
  onAudioSelect,
  selectedAudioIndex = null,
}) => {
  const [copySuccess, setCopySuccess] = useState({ filename: false, url: false })
  const [audioSize, setAudioSize] = useState(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const audioRef = useRef(null)

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i]
  }

  useEffect(() => {
    if (!audioUrl) return
    fetch(audioUrl, { method: "HEAD" })
      .then((res) => {
        const len = res.headers.get("content-length")
        if (len) setAudioSize(formatFileSize(parseInt(len)))
      })
      .catch(() => {})
  }, [audioUrl])

  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, onClose])

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess((prev) => ({ ...prev, [type]: true }))
      setTimeout(() => setCopySuccess((prev) => ({ ...prev, [type]: false })), 2000)
    })
  }

  const handleMediaSelect = (file) => {
    if (!file) return
    const filePath = file.path || file.url
    if (!filePath) return
    onAudioSelect?.({ path: filePath, selectedIndex: selectedAudioIndex, fromPreview: true })
    setIsMediaLibraryOpen(false)
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000]">
      <div className="bg-admin_paper rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-admin_bordered">
          <span className="text-admin_text font-semibold truncate max-w-[calc(100%-40px)]">
            {fileName || "Audio Preview"}
          </span>
          <button onClick={onClose} className="text-admin_text_secondary hover:text-admin_text ml-3 flex-shrink-0">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Player */}
        <div className="px-5 py-8 flex flex-col items-center gap-6">
          {/* Waveform / music icon */}
          <div className="w-24 h-24 rounded-full bg-admin_neutral_2 flex items-center justify-center">
            <svg className="w-12 h-12 text-admin_text_secondary" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6zm-2 16a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
            </svg>
          </div>

          {/* Native audio player */}
          <audio
            ref={audioRef}
            src={audioUrl}
            controls
            className="w-full"
          />

          {/* Meta */}
          {audioSize && (
            <p className="text-sm text-admin_text_secondary">{audioSize}</p>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              variant="admin_secondary"
              className="flex-1 flex items-center justify-center gap-2"
              onClick={() => copyToClipboard(fileName, "filename")}
            >
              <ClipboardIcon className="w-4 h-4" />
              {copySuccess.filename ? "Copied!" : "Copy filename"}
            </Button>
            <Button
              variant="admin_secondary"
              className="flex-1 flex items-center justify-center gap-2"
              onClick={() => copyToClipboard(audioUrl, "url")}
            >
              <ClipboardIcon className="w-4 h-4" />
              {copySuccess.url ? "Copied!" : "Copy URL"}
            </Button>
          </div>

          <Button
            variant="admin_primary"
            className="w-full"
            onClick={() => setIsMediaLibraryOpen(true)}
          >
            Replace Audio
          </Button>
        </div>
      </div>

      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onFileSelect={handleMediaSelect}
        title="Replace Audio"
        defaultFileType="audio"
      />
    </div>,
    document.body
  )
}

export default AudioPreview
